/**
 * Per-room wrapper around the shared `computePaymentSchedule` helper. Pure + DOM-free so both the web
 * checkout and the React Native checkout (ELARA-717) derive the pay-now / pay-later split identically.
 *
 * Pulls deposit data off the `RoomRateModel` and feeds the pure helper. Returns `undefined` when the
 * rate is missing so the caller can hide the section. The cross-room fold (`combinePaymentSchedules`)
 * and its ordering convention live in `@elara/domain/payment-schedule` so the live checkout preview and
 * the persisted trip-level schedule stay identical.
 */

import type { RoomRateModel } from '#models/RoomRateModel.js';
import type { PriceSnapshotItem } from '@elara/domain/models/schemas/booking-schemas';
import type { PaymentScheduleEntry } from '@elara/domain/models/schemas/itinerary-item-schemas';

import { computePaymentSchedule } from '@elara/domain/payment-schedule';

export function getPaymentScheduleForRoom(args: {
  rate: RoomRateModel | undefined;
  priceSnapshot: PriceSnapshotItem | undefined;
  checkInDate: string;
  checkOutDate: string;
}): PaymentScheduleEntry[] | undefined {
  const { rate, priceSnapshot, checkInDate, checkOutDate } = args;
  if (!rate || !priceSnapshot) return undefined;
  return computePaymentSchedule({
    total: priceSnapshot.totalCost,
    prePaymentRequired: rate.prePaymentRequired,
    depositRequired: rate.depositRequired,
    refundable: rate.refundable,
    depositPolicies: rate.depositPolicies,
    checkInDate,
    checkOutDate,
  });
}
